import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="privacy-policy-page">
      <Navbar />

      {/* Content */}
      <main className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-slate prose-lg max-w-none">
            <p className="text-slate-600 leading-relaxed mb-8">
              This Privacy Policy outlines how your data is handled when you use the Shopify app developed and managed by Arismeta Technology Services Pvt. Ltd. in association with SimplyPhi Software Solutions Pvt. Ltd. (operating as "Emly Labs"). By accessing or using the app or associated dashboard (the "Platform"), you consent to the practices described in this Privacy Policy.
            </p>

            <p className="text-slate-600 leading-relaxed mb-8">
              Arismeta Technology Services Pvt. Ltd. manages the app's integration with Shopify APIs, handles user authentication and consent, and oversees the secure access of store data. Arismeta Technology Services Pvt. Ltd. is responsible for ensuring compliance with Shopify's platform policies, maintaining the app listing, and handling customer relationships.
            </p>

            <p className="text-slate-600 leading-relaxed mb-8">
              SimplyPhi Software Solutions Pvt. Ltd., under the brand name Emly Labs, serves as Arismeta's technology and infrastructure partner. Emly Labs is responsible for hosting and managing the app dashboard, processing the data accessed via Shopify APIs, and delivering AI-powered features and analytics. Emly Labs does not use, share, or modify user-uploaded or Shopify-sourced data except as necessary to provide services on behalf of Arismeta.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Information We Collect</h2>
            
            <div className="space-y-4 mb-8">
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">User Account Information</h3>
                <p className="text-slate-600">When you create an account on the Platform, we may collect personal information, such as your name, email address, contact details, and other identifiers.</p>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">Usage Information</h3>
                <p className="text-slate-600">We collect non-personal information about your interactions with the Platform, including your IP address, browser type, operating system, and referring website.</p>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">Cookies and Tracking Technologies</h3>
                <p className="text-slate-600">Emly Labs uses cookies and similar tracking technologies to enhance your experience on the Platform. Cookies help us analyze user behavior, customize content, and remember your preferences.</p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Information We DO NOT Collect</h2>
            
            <p className="text-slate-600 leading-relaxed mb-4">
              Emly Labs does not collect, share, view, modify, or use uploaded data for any purpose other than providing the services you have requested.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-4">
              "Uploaded Data" refers to the digital information, files or content that can originate from users, third-party software systems, local storage, or other sources, and is voluntarily submitted or transferred to the platform for the purpose of analysis and processing by the platform.
            </p>

            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>Uploaded Data does not include any of the following:</strong>
            </p>
            
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-8 pl-4">
              <li>"Data provided by Emly," which refers to digital information, files, or content that is generated, processed, or made available directly by Emly Labs, distinct from data uploaded by users.</li>
              <li>"User Account Information," which includes personal information such as your name, email address, contact details, and other identifiers collected when an account is created on the Platform.</li>
              <li>"Usage Information," which consists of non-personal information about your interactions with the Platform, including your IP address, browser type, operating system, and referring website.</li>
              <li>"Cookies and Tracking Technologies," which are used by Emly Labs to enhance your experience on the Platform, analyze user behavior, customize content, and remember your preferences.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">How We Ensure Data Privacy</h2>
            
            <div className="space-y-4 mb-8">
              <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
                <h3 className="font-semibold text-emerald-900 mb-2">Data Isolation</h3>
                <p className="text-emerald-800">Emly Labs has implemented strict data isolation measures to ensure that the data you upload is segregated from any other user's data and cannot be accessed or used for any unauthorized purposes.</p>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
                <h3 className="font-semibold text-emerald-900 mb-2">Security</h3>
                <p className="text-emerald-800">We take data security seriously and have put in place robust security measures to protect your uploaded data from unauthorized access or breaches.</p>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
                <h3 className="font-semibold text-emerald-900 mb-2">No Data Usage</h3>
                <p className="text-emerald-800">Emly Labs employees, contractors, or third-party service providers do not have access to your uploaded data and do not use it for any purposes other than providing the services you have requested.</p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Your Choices</h2>
            
            <div className="space-y-4 mb-8">
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">Data Upload</h3>
                <p className="text-slate-600">You control the data you upload to the Platform. Please ensure that you have the right and authority to upload and use this data.</p>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-2">Data Deletion</h3>
                <p className="text-slate-600">You have the right to request the deletion of your uploaded data from the Platform. To do so, please contact us at admin@emlylabs.com</p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Data Security</h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Emly Labs takes rigorous measures to secure your data and personal information. Our commitment to data non-usage means that your uploaded data is safeguarded from unauthorized access or use.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              The Platform is not intended for children under the age of 13. We do not knowingly collect or maintain personal information from children.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Changes to this Privacy Policy</h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Emly Labs may update this Privacy Policy to reflect changes in our practices or legal requirements. We will post the updated policy on the Platform, and the date of the latest revision will be indicated.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Contact Information</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have questions or concerns about this Privacy Policy or your data, you may contact:
            </p>
            
            <div className="space-y-2 mb-8">
              <p className="text-slate-600">
                <strong>Emly Labs (Platform & Dashboard Management):</strong>{' '}
                <a href="mailto:admin@emlylabs.com" className="text-emerald-600 hover:text-emerald-700">admin@emlylabs.com</a>
              </p>
              <p className="text-slate-600">
                <strong>Arismeta Technology Services Pvt. Ltd. (Shopify App Ownership):</strong>{' '}
                <a href="mailto:developer@arismeta.com" className="text-emerald-600 hover:text-emerald-700">developer@arismeta.com</a>
              </p>
            </div>

            <div className="bg-slate-900 text-white rounded-xl p-6 mt-12">
              <p className="text-slate-300">
                <strong className="text-white">By using the Platform, you acknowledge that you have read and agree to the terms of this Privacy Policy.</strong>
              </p>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="mt-16 text-center">
            <Link to="/">
              <Button 
                size="lg" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                data-testid="back-to-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
